import { endpoints } from '../api';
import type { Me, Voice, VoiceCollection } from '../api/types';
import type { PlanCatalog } from '../api/commerce';
import { applyRuntimePreferences } from '../features/settings/runtime-preferences';
import { steosIdServer } from '../features/profile/steos-id-server';
import { clearPeople, connectPeople, syncPeople } from '../features/profile/people-server';
import { accountLocale } from '../i18n';
import { setCatalogTotal } from './catalog-total';
import { readHistoryRepeat } from './history-repeat';
import { readShellMemory, shellMemoryKey } from './shell-memory';
import { portraitOrb, voiceOrb } from './voice-orb';
import { loadCharDetails, loadCharVoices, loadServerChars, type ShellChar } from './server-characters';
import { favorites } from './favorites';

interface ShellVoice {
  id: number;
  name: string;
  tags: string[];
  sex: string | null;
  orb: string;
  /** Шар с аватаркой полного размера — для героя карточки голоса; orb — с превью, для списков */
  orbFull: string;
  c: string[];
  mine: boolean;
  isNew: boolean;
  is_s2s: boolean;
  preview_url: string | null;
  avatar_url: string | null;
  top: boolean;
  ch?: number;
  /** Актёр голоса (D-270): строка «Актёр» в карточке голоса */
  act?: number;
}

function shellVoice(voice: Voice): ShellVoice {
  return {
    id: voice.id,
    name: voice.name,
    tags: voice.tags,
    sex: voice.sex,
    orb: voiceOrb(voice) ?? '',
    orbFull: voiceOrb(voice, true) ?? '',
    c: [],
    mine: voice.is_mine,
    isNew: voice.is_new,
    is_s2s: voice.is_s2s,
    preview_url: voice.preview_url,
    avatar_url: voice.avatar_url,
    top: !voice.is_mine,
    /* голос знает своего персонажа, как в шаблоне (v.ch): строка «Голос персонажа», карточка персонажа */
    ch: voice.character_id ?? undefined,
    act: voice.actor_id ?? undefined,
  };
}

export interface ShellCollection { id: string; name: string; desc: string; img?: string; ic: string; count: number; charCount?: number; faces: string[] }

/** Подборка сервера (§4.5) карточкой шаблона: обложка — картинка или шар cover_orb, кружки — шары голосов preview_voices (до четырёх) */
export function shellCollection(item: VoiceCollection & { id: number }): ShellCollection {
  const orb = item.cover_orb;
  return {
    id: String(item.id), name: item.name, desc: item.description ?? '',
    img: item.cover_thumb_url ?? item.cover_url ?? undefined,
    ic: orb ? `conic-gradient(from 210deg at 50% 50%,${orb.from},${orb.to},${orb.from})` : 'linear-gradient(135deg,#7854a8,#372965)',
    count: item.voice_count,
    charCount: item.avatar_count || undefined,
    faces: (item.preview_voices ?? []).slice(0, 4).map(v => voiceOrb(v)).filter((f): f is string => !!f),
  };
}

const PLAN_WAIT_MS = 1500;

/** Цена месяца самого дешёвого тарифа в Stars (тестовые тарифы dev_only не считаются); нет месячной цены — null */
export function minPlanStars(catalog: PlanCatalog): number | null {
  const prices = catalog.items
    .filter(plan => !plan.dev_only)
    .flatMap(plan => plan.periods.filter(period => period.months === 1).map(period => period.stars))
    .filter(stars => Number.isFinite(stars) && stars > 0);
  return prices.length ? Math.min(...prices) : null;
}

/** Общий лист STEOS ID (BotCore.sid) входит через сервер; состояние подключения идёт за каждым новым снимком Me */
function installSteosId(me: Me) {
  const sid = window.BotCore.sid;
  sid.connect(steosIdServer);
  sid.sync(me.steos_id);
  document.addEventListener('steos:account-updated', event => {
    const next = (event as CustomEvent<Me | undefined>).detail;
    if (next?.steos_id) sid.sync(next.steos_id);
  });
  steosIdServer.resume();
}

/** Runs before mounting the shell: server voice IDs and selection replace prototype fixtures. */
export async function installServerState() {
  const session = await endpoints.session();
  // Недоступность необязательных настроек не мешает входу в приложение.
  try { applyRuntimePreferences(await endpoints.account.settings()); } catch { /* Настройки перечитаются на своей странице. */ }
  /* снимок каталога в сессии — на языке профиля: после смены языка он уже не годится (BotVoiceCatalog) */
  const catalogLocale = accountLocale();
  /* Бейдж «Баланс» в меню без подписки — «От ★ N», цена самого дешёвого тарифа (решение владельца 05.10.2026). Тарифы идут параллельно
     с голосами; не успели — бейдж «Подписка», как раньше: демо-цены шаблона в серверный режим не попадают */
  const planFrom = Promise.race([
    (async () => { try { return minPlanStars(await endpoints.commerce.plans()); } catch { return null; } })(),
    new Promise<null>(done => setTimeout(() => done(null), PLAN_WAIT_MS)),
  ]);
  /* Как шаблон, приложение открывается сразу: весь каталог заранее не выкачивается. Каркасу хватает первой страницы из сессии,
     остальное список догружает сам (BotVoiceCatalog ниже) и складывает в тот же снимок. Выбранный голос и голос повтора
     из «Истории» нужны каркасу по id ещё до первой прокрутки — их карточки берутся отдельно, если их нет на первой странице. */
  /* персонажи каталога — параллельно с голосами: вкладка «Персонажи» и карточка персонажа, как у шаблона */
  const charsLoad = loadServerChars(portraitOrb);
  /* без снимка в сессии первая страница идёт запросом; её catalog_total (весь каталог, §4.2) — то же число, что у снимка */
  const first = session.catalog ?? (await endpoints.voices({ tab: 'reco', sort: 'pop', limit: 14 }));
  const voices: Voice[] = [...first.items];
  const repeat = readHistoryRepeat('tts', session.me.user?.id);
  const wanted = [session.me.tts_voice_id, repeat?.voice_id ?? Number(repeat?.inputs?.voice_id)]
    .filter((id): id is number => Number.isSafeInteger(id) && (id as number) > 0);
  await Promise.all([...new Set(wanted)].filter(id => !voices.some(v => v.id === id)).map(id =>
    endpoints.voiceDetails(id).then(v => { voices.push(v); }, () => undefined)));
  const w = window as unknown as {
    BotDemo: { VOICES: ShellVoice[]; VCOLLS: unknown[]; CVCOLLS?: ShellCollection[]; CHARS: unknown[]; ACTORS?: unknown[]; MYV: unknown[]; VTOTAL: number };
    BotClone?: { voices: () => ShellVoice[]; load?: () => Promise<void> };
    BotServerMe?: { balance: { circle: number | null } };
    BotVoiceCatalog?: (state: { tab: string; q: string; sort: string; coll?: string | null; only?: 's2s' | '' }, cursor?: string, only?: { s2s?: boolean }) => Promise<{
      items: ShellVoice[]; nextCursor: string | null; total: number; catalogTotal: number;
      collections?: ShellCollection[]; characterIds?: number[];
    }>;
    BotVoiceCollections?: () => Promise<ShellCollection[]>;
    BotVoiceCollectionsTop?: (n: number) => Promise<ShellCollection[]>;
  };
  const mapped = voices.map(shellVoice);
  w.BotServerMe = session.me;
  (window as unknown as { BotServerPlanFrom?: number | null }).BotServerPlanFrom = await planFrom;
  const prototypeBar = document.querySelector<HTMLElement>('.topbar');
  if (prototypeBar) prototypeBar.style.display = 'none';
  w.BotDemo.VOICES.splice(0, w.BotDemo.VOICES.length, ...mapped);
  w.BotDemo.MYV.splice(0); // Свои клоны берём из API, демо-голоса не подмешиваем в серверный каталог.
  w.BotDemo.VCOLLS.splice(0);
  // Вымышленные персонажи шаблона в серверный режим не попадают: только каталог сервера (пустой — вкладки «Персонажи» нет).
  /* реальные люди (D-270) — отдельным списком для вкладки «Актёры»: CHARS читают ряды лиц и роли всех экранов */
  const putChars = (chars: ShellChar[]) => {
    w.BotDemo.CHARS.splice(0, w.BotDemo.CHARS.length, ...chars.filter(c => !c.actor));
    (w.BotDemo.ACTORS ||= []).splice(0, w.BotDemo.ACTORS.length, ...chars.filter(c => c.actor));
  };
  putChars(await charsLoad);
  /* смена языка профиля: каталог персонажей — заново на новом языке (имена, источники, подборки), список голосов дорисует вкладку (steos:chars) */
  document.addEventListener('steos:locale', () => {
    void loadServerChars(portraitOrb).then(chars => {
      if (!chars.length) return;
      putChars(chars);
      document.dispatchEvent(new Event('steos:chars'));
    });
  });
  (window as unknown as { BotServerCharDetails?: (id: number) => Promise<boolean> }).BotServerCharDetails = id => {
    const c = ([...w.BotDemo.CHARS, ...(w.BotDemo.ACTORS ?? [])] as ShellChar[]).find(x => x.id === id);
    if (!c) return Promise.resolve(false);
    const known = (voiceId: number) => w.BotDemo.VOICES.some(v => v.id === voiceId);
    const add = (voice: Voice) => { w.BotDemo.VOICES.push(shellVoice(voice)); };
    /* голоса приходят строками в карточке персонажа (voice_cards) — одним запросом; по одному (тяжёлая карточка голоса на каждый)
       догружаются только недостающие: старый бэкенд без поля или сбой сервиса голосов */
    return loadCharDetails(c, cards => {
      const fresh = cards.filter(voice => !known(voice.id));
      fresh.forEach(add);
      return fresh.length > 0;
    }).then(details =>
      loadCharVoices(c, known, voiceId => endpoints.voiceDetails(voiceId), add).then(voices => details || voices),
    );
  };
  w.BotDemo.VTOTAL = first.catalog_total ?? voices.length;
  setCatalogTotal(first.catalog_total);
  // Voice currently accepts collection IDs. System-code filtering is a separate capability.
  const toShell = (items: VoiceCollection[]) =>
    items.flatMap(item => (item.id === null ? [] : [shellCollection({ ...item, id: item.id })]));
  /* Подборки грузились постранично и целиком, поэтому «Подборки» и поиск ждали все страницы подряд. Запрос запоминается на язык профиля
     (после смены языка названия другие), сбой не запоминается. «Рекомендуем» берёт только первые n — одной короткой страницей */
  const memo = <T>(load: () => Promise<T>) => {
    let hit: { locale: string; p: Promise<T> } | null = null;
    return () => {
      const locale = accountLocale();
      if (!hit || hit.locale !== locale) {
        const mine = { locale, p: load() };
        hit = mine;
        mine.p.catch(() => { if (hit === mine) hit = null; });
      }
      return hit.p;
    };
  };
  const allCollections = memo(async () => {
    const result: ShellCollection[] = [];
    let next: string | undefined;
    do {
      const page = await endpoints.voiceCollections(next);
      result.push(...toShell(page.items));
      next = page.next_cursor ?? undefined;
    } while (next);
    return result;
  });
  const topCollections = new Map<number, () => Promise<ShellCollection[]>>();
  w.BotVoiceCollections = allCollections;
  w.BotVoiceCollectionsTop = n => {
    if (!topCollections.has(n)) topCollections.set(n, memo(async () => toShell((await endpoints.voiceCollections(undefined, n)).items)));
    return topCollections.get(n)!();
  };
  /* прогрев: к открытию «Озвучки» подборки уже загружены — сначала короткая страница для «Рекомендуем», затем весь набор */
  /* подборки персонажей — подборки голосов, в которых стоят персонажи (Q-291): каталог персонажей экранов («Кто говорит», роли) берёт их отсюда;
     после смены языка — заново на новом языке */
  const keepCharColls = () => allCollections().then(list => { w.BotDemo.CVCOLLS = list; }, () => undefined);
  void w.BotVoiceCollectionsTop(3).catch(() => undefined).then(() => keepCharColls());
  document.addEventListener('steos:locale', () => { void keepCharColls(); });
  w.BotVoiceCatalog = async (state, nextCursor, only) => {
    const q = state.q.trim();
    const tab = q ? 'search' : state.coll ? 'collection' : state.tab;
    /* отбор переозвучки: у экранов «только с переозвучкой» — третьим аргументом, у листа ⇅ «Озвучки» — в состоянии списка */
    const s2s = !!only?.s2s || state.only === 's2s';
    /* подборка «Избранное» (bridge/favorites.ts): голоса — уже в каталоге каркаса, порядок — новые первыми; сортировка ⇅ — на месте */
    if (!q && state.coll === 'fav') {
      const byId = new Map(w.BotDemo.VOICES.map(v => [v.id, v]));
      const items = favorites.list('voice').map(id => byId.get(id)).filter((v): v is ShellVoice => !!v && (!s2s || v.is_s2s));
      const locale = accountLocale();
      if (state.sort === 'name') items.sort((p, n) => p.name.localeCompare(n.name, locale));
      else if (state.sort === 'name_desc') items.sort((p, n) => n.name.localeCompare(p.name, locale));
      else if (state.sort === 'new') items.sort((p, n) => Number(n.isNew) - Number(p.isNew));
      return { items, nextCursor: null, total: items.length, catalogTotal: w.BotDemo.VTOTAL };
    }
    if (tab !== 'reco' && tab !== 'new' && tab !== 'mine' && tab !== 'search' && tab !== 'collection')
      return { items: [], nextCursor: null, total: 0, catalogTotal: w.BotDemo.VTOTAL };
    // Снимок сессии не отобран по переозвучке: при отборе первая страница тоже идёт запросом.
    // Снимок — на языке входа: после смены языка профиля «Рекомендуем» тоже идёт запросом (названия на новом языке)
    if (tab === 'reco' && !nextCursor && session.catalog && !s2s && accountLocale() === catalogLocale) {
      return {
        items: session.catalog.items.map(shellVoice),
        nextCursor: session.catalog.next_cursor ?? null,
        total: session.catalog.total,
        catalogTotal: session.catalog.catalog_total ?? w.BotDemo.VTOTAL,
        collections: session.catalog.collections && toShell(session.catalog.collections),
        characterIds: session.catalog.character_ids,
      };
    }
    const page = await endpoints.voices({
      tab, q: q || undefined, sort: tab === 'reco' ? 'pop' : state.sort as 'pop' | 'new' | 'name' | 'name_desc',
      collection_id: tab === 'collection' ? Number(state.coll) : undefined,
      can_s2s: s2s || undefined,
      limit: 14, cursor: nextCursor,
    });
    const items = page.items.map(shellVoice);
    for (const item of items) {
      const known = w.BotDemo.VOICES.find(v => v.id === item.id);
      if (!known) w.BotDemo.VOICES.push(item);
      else Object.assign(known, { name: item.name, tags: item.tags }); // названия — на языке последнего запроса
    }
    // Число отобранного списка не становится размером всего каталога у вкладки «Озвучка»
    if (!s2s) {
      w.BotDemo.VTOTAL = page.catalog_total;
      setCatalogTotal(page.catalog_total);
    }
    return {
      items, nextCursor: page.next_cursor, total: page.total, catalogTotal: page.catalog_total,
      collections: page.collections && toShell(page.collections), characterIds: page.character_ids,
    };
  };
  /* избранное не задерживает вход: голоса избранного встают в каталог каркаса (подборка «Избранное» рисуется без догрузки), затем steos:fav */
  void favorites.load().then(list => {
    for (const voice of list) if (!w.BotDemo.VOICES.some(v => v.id === voice.id)) w.BotDemo.VOICES.push(shellVoice(voice));
    document.dispatchEvent(new CustomEvent('steos:fav'));
  }, () => undefined);
  if (w.BotClone) {
    // Список клонов грузится заранее и не задерживает вход: раздел дождётся того же запроса.
    void w.BotClone.load?.();
  }
  installSteosId(session.me);
  // «Вы» и «Близкие» общие для всех экранов: демо прототипа уходит сразу, данные сервера приходят следом.
  clearPeople();
  connectPeople();
  void syncPeople().catch(() => undefined);
  /* Память этого владельца (раздел, вкладка и прокрутка списка) остаётся, как после перезагрузки у шаблона; голос — всегда серверный */
  const owner = session.me.user?.id;
  const prev = readShellMemory(owner);
  const stored = { ...prev, sec: prev?.sec ?? 'tts', tts: { mem: { coll: null, pos: {} }, ...prev?.tts, voice: session.me.tts_voice_id, engine: session.me.tts_engine ?? 'v2' } };
  sessionStorage.setItem(shellMemoryKey(owner), JSON.stringify(stored));
}
